import { useCallback, useEffect, useMemo, useState } from 'react';

import { getPolicies, type PolicySummary } from '../api';
import {
  searchCommerceReferences,
  type CommerceReference,
  type ReferenceKind,
} from '../commerce/referenceApi';
import { getCommercialPreview, type CommercialPreview } from './previewApi';

function eventValue(event: Event): string {
  return (event.currentTarget as HTMLElement & { value: string }).value;
}

function optionLabel(reference: CommerceReference): string {
  return reference.detail ? `${reference.label} — ${reference.detail}` : reference.label;
}

function useReferenceSearch(kind: ReferenceKind) {
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [results, setResults] = useState<CommerceReference[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError(null);
      void searchCommerceReferences(kind, query)
        .then((references) => {
          if (!active) return;
          setResults(references);
          if (selectedId && !references.some((item) => item.id === selectedId)) {
            setResults((current) => [
              ...current,
              { id: selectedId, label: 'Current selection', detail: selectedId },
            ]);
          }
        })
        .catch((reason: unknown) => {
          if (!active) return;
          setError(reason instanceof Error ? reason.message : 'Search failed.');
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 300);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [kind, query, selectedId]);

  const clear = useCallback(() => {
    setQuery('');
    setSelectedId('');
    setResults([]);
    setError(null);
  }, []);

  return {
    query,
    setQuery,
    selectedId,
    setSelectedId,
    results,
    loading,
    error,
    clear,
  };
}

export function PreviewPanel() {
  const locations = useReferenceSearch('company_location');
  const variants = useReferenceSearch('variant');
  const [policies, setPolicies] = useState<PolicySummary[]>([]);
  const [draftPolicyId, setDraftPolicyId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [preview, setPreview] = useState<CommercialPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void getPolicies()
      .then((rows) => {
        if (active) setPolicies(rows.filter((item) => item.draft_version !== null));
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  const canPreview = useMemo(() => {
    const parsed = Number(quantity);
    return Boolean(
      locations.selectedId &&
        variants.selectedId &&
        Number.isInteger(parsed) &&
        parsed >= 1,
    );
  }, [locations.selectedId, quantity, variants.selectedId]);

  const runPreview = useCallback(async () => {
    setError(null);
    setPreview(null);
    const parsedQuantity = Number(quantity);
    if (!canPreview || !Number.isInteger(parsedQuantity)) {
      setError('Select a company location, product variant, and valid quantity first.');
      return;
    }

    setLoading(true);
    try {
      const result = await getCommercialPreview({
        company_location_id: locations.selectedId,
        variant_id: variants.selectedId,
        quantity: parsedQuantity,
        draft_policy_id: draftPolicyId || null,
      });
      setPreview(result);
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : 'Could not build preview.');
    } finally {
      setLoading(false);
    }
  }, [
    canPreview,
    draftPolicyId,
    locations.selectedId,
    quantity,
    variants.selectedId,
  ]);

  const priceDelta = useMemo(() => {
    if (!preview?.proposed_overlay) return null;
    const current = Number(preview.shopify_current.effective_unit_price);
    const proposed = Number(preview.proposed_overlay.final_price);
    if (!Number.isFinite(current) || !Number.isFinite(proposed)) return null;
    const amount = proposed - current;
    const percent = current === 0 ? null : (amount / current) * 100;
    return { amount, percent };
  }, [preview]);

  return (
    <s-section heading="Preview as B2B buyer">
      <s-stack direction="block" gap="base">
        <s-paragraph>
          See Shopify's current company-location price and quantity rules, then compare a draft
          Atellius rule against that exact buyer context. Preview is read-only.
        </s-paragraph>

        <s-heading>Buyer</s-heading>
        <s-text-field
          label="Search company location"
          value={locations.query}
          placeholder="Type a company or location name"
          onInput={(event) => locations.setQuery(eventValue(event))}
        />
        <s-select
          label="Company location"
          value={locations.selectedId}
          disabled={locations.loading}
          onChange={(event) => {
            locations.setSelectedId(eventValue(event));
            setPreview(null);
          }}
        >
          <s-option value="">
            {locations.loading ? 'Searching…' : 'Choose a company location'}
          </s-option>
          {locations.results.map((reference) => (
            <s-option key={reference.id} value={reference.id}>
              {optionLabel(reference)}
            </s-option>
          ))}
        </s-select>
        {locations.error && <s-paragraph>Buyer search: {locations.error}</s-paragraph>}

        <s-heading>Product</s-heading>
        <s-text-field
          label="Search product or SKU"
          value={variants.query}
          placeholder="Type product title, variant, or SKU"
          onInput={(event) => variants.setQuery(eventValue(event))}
        />
        <s-select
          label="Product variant"
          value={variants.selectedId}
          disabled={variants.loading}
          onChange={(event) => {
            variants.setSelectedId(eventValue(event));
            setPreview(null);
          }}
        >
          <s-option value="">
            {variants.loading ? 'Searching…' : 'Choose a product variant'}
          </s-option>
          {variants.results.map((reference) => (
            <s-option key={reference.id} value={reference.id}>
              {optionLabel(reference)}
            </s-option>
          ))}
        </s-select>
        {variants.error && <s-paragraph>Product search: {variants.error}</s-paragraph>}

        <s-number-field
          label="Quantity"
          value={quantity}
          min={1}
          step={1}
          onInput={(event) => {
            setQuantity(eventValue(event));
            setPreview(null);
          }}
        />

        <s-select
          label="Draft rule to compare (optional)"
          value={draftPolicyId}
          onChange={(event) => {
            setDraftPolicyId(eventValue(event));
            setPreview(null);
          }}
        >
          <s-option value="">Shopify current state only</s-option>
          {policies.map((policy) => (
            <s-option key={policy.id} value={policy.id}>
              {policy.name} — draft v{policy.draft_version}
            </s-option>
          ))}
        </s-select>

        <s-button onClick={() => void runPreview()} disabled={!canPreview || loading}>
          {loading ? 'Building preview…' : 'Preview buyer pricing'}
        </s-button>
        {error && <s-paragraph>Preview needs attention: {error}</s-paragraph>}

        {preview && (
          <s-stack direction="block" gap="small">
            <s-heading>
              {preview.buyer.company_name} — {preview.buyer.company_location_name}
            </s-heading>
            <s-paragraph>
              {preview.product.product_title} · {preview.product.variant_title}
              {preview.product.sku ? ` · SKU ${preview.product.sku}` : ''}
            </s-paragraph>
            <s-paragraph>
              Active B2B catalogs: {preview.buyer.active_catalogs.length}
            </s-paragraph>

            <s-heading>Shopify current</s-heading>
            <s-paragraph>
              Effective unit price at quantity {preview.quantity}: {preview.shopify_current.currency}{' '}
              {preview.shopify_current.effective_unit_price}
            </s-paragraph>
            <s-paragraph>
              Quantity rule: min {preview.shopify_current.quantity_rule.minimum} · max{' '}
              {preview.shopify_current.quantity_rule.maximum ?? 'none'} · increment{' '}
              {preview.shopify_current.quantity_rule.increment}
            </s-paragraph>
            {preview.shopify_current.quantity_violations.map((item) => (
              <s-paragraph key={item}>Shopify quantity issue: {item}</s-paragraph>
            ))}
            {preview.shopify_current.quantity_breaks.length > 0 && (
              <s-paragraph>
                {preview.shopify_current.quantity_breaks.length} Shopify quantity price break(s)
                detected.
              </s-paragraph>
            )}

            {preview.proposed_overlay && (
              <s-stack direction="block" gap="small">
                <s-heading>With draft: {preview.draft?.name}</s-heading>
                <s-paragraph>
                  Proposed unit price: {preview.proposed_overlay.currency}{' '}
                  {preview.proposed_overlay.final_price}
                </s-paragraph>
                {priceDelta && (
                  <s-paragraph>
                    Change from Shopify current: {priceDelta.amount >= 0 ? '+' : ''}
                    {priceDelta.amount.toFixed(2)}
                    {priceDelta.percent !== null
                      ? ` (${priceDelta.percent >= 0 ? '+' : ''}${priceDelta.percent.toFixed(2)}%)`
                      : ''}
                  </s-paragraph>
                )}
                <s-paragraph>
                  {preview.proposed_overlay.safe_for_this_context
                    ? 'No Atellius rule conflict was detected in this buyer/product/quantity context.'
                    : 'Atellius detected a rule conflict in this context. Do not publish this draft.'}
                </s-paragraph>
                {preview.proposed_overlay.trace.map((entry) => (
                  <s-paragraph key={`${entry.policy_key}-${entry.version_number}`}>
                    {entry.policy_key}: {entry.reason}
                    {entry.applied_actions.length > 0
                      ? ` · applied ${entry.applied_actions.join(', ')}`
                      : ''}
                  </s-paragraph>
                ))}
                {preview.proposed_overlay.conflicts.map((conflict) => (
                  <s-paragraph key={`${conflict.family}-${conflict.priority}`}>
                    Conflict: {conflict.message}
                  </s-paragraph>
                ))}
              </s-stack>
            )}

            <s-paragraph>{preview.semantics}</s-paragraph>
          </s-stack>
        )}
      </s-stack>
    </s-section>
  );
}
